import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { RubricDragContext } from '../Context';
import { isRubricDrag, getRubricDropTarget, isRubricMoveNoop } from '../../utils/rubricDrop';

const accentColor = (isDark) => (isDark ? '#4fc3f7' : '#1976d2');

// Défilement automatique quand le pointeur approche du bord haut ou bas d'une zone (px, px par image)
const AUTO_SCROLL_EDGE = 72;
const AUTO_SCROLL_MAX_SPEED = 18;

/**
 * Survol d'une zone de dépôt pendant un glissement de rubrique.
 * hover(event, value) se place dans le dragover de la zone ; target vaut la dernière valeur, null hors survol.
 */
function useRubricDragHover({ autoScroll = false } = {}) {
  const { dragItem } = useContext(RubricDragContext);
  const [target, setTarget] = useState(null);
  const containerRef = useRef(null);
  const scrollRef = useRef({ element: null, speed: 0, frame: null });

  const stopAutoScroll = useCallback(() => {
    const scroll = scrollRef.current;
    scroll.speed = 0;
    if (scroll.frame) cancelAnimationFrame(scroll.frame);
    scroll.frame = null;
  }, []);

  const clear = useCallback(() => {
    setTarget(null);
    stopAutoScroll();
  }, [stopAutoScroll]);

  // Le dragleave seul peut manquer la sortie vers une zone voisine (bords de colonnes en fractions
  // de pixel, coordonnées arrondies) : tout dragover hors du conteneur efface le survol
  useEffect(() => {
    if (!dragItem) {
      clear();
      return undefined;
    }
    const clearWhenOutside = (event) => {
      const container = containerRef.current;
      if (container && !container.contains(event.target)) clear();
    };
    document.addEventListener('dragover', clearWhenOutside, true);
    return () => document.removeEventListener('dragover', clearWhenOutside, true);
  }, [dragItem, clear]);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const updateAutoScroll = (element, clientY) => {
    const rect = element.getBoundingClientRect();
    const edge = Math.min(AUTO_SCROLL_EDGE, rect.height / 4);
    let ratio = 0;
    if (clientY < rect.top + edge) ratio = -(rect.top + edge - clientY) / edge;
    else if (clientY > rect.bottom - edge) ratio = (clientY - (rect.bottom - edge)) / edge;

    const scroll = scrollRef.current;
    scroll.element = element;
    scroll.speed = ratio
      ? Math.sign(ratio) * Math.max(1, Math.round(Math.min(Math.abs(ratio), 1) * AUTO_SCROLL_MAX_SPEED))
      : 0;
    if (!scroll.speed || scroll.frame) return;

    const step = () => {
      const previous = scroll.element.scrollTop;
      if (scroll.speed) scroll.element.scrollTop += scroll.speed;
      // Arrêt en butée : le prochain dragover relance le défilement si besoin
      scroll.frame = scroll.speed && scroll.element.scrollTop !== previous ? requestAnimationFrame(step) : null;
    };
    scroll.frame = requestAnimationFrame(step);
  };

  const hover = (event, value) => {
    containerRef.current = event.currentTarget;
    setTarget(value);
    if (autoScroll) updateAutoScroll(event.currentTarget, event.clientY);
  };

  // dragleave remonte aussi des descendants : on n'efface qu'en sortant réellement du conteneur
  // (utile quand le pointeur quitte la fenêtre, où plus aucun dragover n'est émis)
  const onDragLeave = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (
      event.clientX <= rect.left || event.clientX >= rect.right ||
      event.clientY <= rect.top || event.clientY >= rect.bottom
    ) {
      clear();
    }
  };

  return { dragItem, target, hover, clear, onDragLeave };
}

/**
 * Zone de dépôt de l'édition et du sommaire.
 * dropZoneProps se pose sur le conteneur (défilant) ; les emplacements d'insertion sont ses descendants
 * marqués data-rubric-slot="<id de rubrique>".
 * Rubrique ou canevas (panneau gauche, discussion) : ajouté à l'emplacement survolé.
 * Rubrique du document : déplacée via onMoveRubric(rubricId, beforeRubricId) ; sans onMoveRubric, refusée.
 * dropIndex : rang d'insertion survolé (0..nombre de rubriques), null hors survol
 * ou quand le dépôt ne changerait pas l'ordre.
 */
export function useRubricDropZone({ onMoveRubric } = {}) {
  const { getDragItem, finishRubricDrag } = useContext(RubricDragContext);
  const { dragItem, target: dropIndex, hover, clear, onDragLeave } = useRubricDragHover({ autoScroll: true });

  const accepts = (item) => Boolean(item) && (item.kind !== 'reportRubric' || Boolean(onMoveRubric));

  const onDragOver = (event) => {
    const item = getDragItem();
    if (!isRubricDrag(event) || !accepts(item)) return;
    event.preventDefault();
    const isMove = item.kind === 'reportRubric';
    event.dataTransfer.dropEffect = isMove ? 'move' : 'copy';
    const { index } = getRubricDropTarget(event.currentTarget, event.clientY);
    hover(event, isMove && isRubricMoveNoop(event.currentTarget, item.rubricId, index) ? null : index);
  };

  const onDrop = (event) => {
    const item = getDragItem();
    if (!isRubricDrag(event) || !accepts(item)) return;
    event.preventDefault();
    const { index, beforeRubricId } = getRubricDropTarget(event.currentTarget, event.clientY);
    finishRubricDrag();
    clear();
    if (item.kind !== 'reportRubric') {
      item.insert?.(beforeRubricId);
    } else if (!isRubricMoveNoop(event.currentTarget, item.rubricId, index)) {
      onMoveRubric(item.rubricId, beforeRubricId);
    }
  };

  return {
    dragItem,
    dropIndex,
    dropZoneProps: { onDragOver, onDragLeave, onDrop }
  };
}

/**
 * Zone de partage de la discussion : l'élément déposé (rubrique ou canevas du panneau gauche,
 * rubrique du document) est transmis à onShare(item). Les encadrés de la discussion n'y sont pas repartagés.
 * onShare null : zone inactive.
 */
export function useRubricShareZone(onShare) {
  const { getDragItem, finishRubricDrag } = useContext(RubricDragContext);
  const { dragItem, target, hover, clear, onDragLeave } = useRubricDragHover();

  const accepts = (item) => Boolean(onShare && item && item.origin !== 'chat');

  const onDragOver = (event) => {
    const item = getDragItem();
    if (!isRubricDrag(event) || !accepts(item)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    hover(event, true);
  };

  const onDrop = (event) => {
    const item = getDragItem();
    if (!isRubricDrag(event) || !accepts(item)) return;
    event.preventDefault();
    finishRubricDrag();
    clear();
    onShare(item);
  };

  return {
    dragItem,
    isOver: target === true,
    dropZoneProps: { onDragOver, onDragLeave, onDrop }
  };
}

/**
 * Trait d'insertion, à placer dans un emplacement en position relative.
 * offset : distance entre le bord de l'emplacement et le milieu de l'espace qui le sépare du voisin.
 */
export function RubricDropIndicator({ placement, offset, label, isDark }) {
  const color = accentColor(isDark);

  return (
    <Box sx={{
      position: 'absolute',
      left: 0,
      right: 0,
      [placement === 'after' ? 'bottom' : 'top']: -offset,
      height: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      pointerEvents: 'none'
    }}>
      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: -1.5,
        height: 3,
        borderRadius: 2,
        backgroundColor: color,
        boxShadow: `0 0 6px ${color}`
      }} />
      {label && (
        <Typography
          component="span"
          sx={{
            position: 'relative',
            maxWidth: '80%',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: color,
            color: isDark ? '#1a252f' : '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

// Contour de dépôt couvrant toute la zone (document sans rubrique, discussion)
export function RubricDropOverlay({ label, isDark, message, zIndex = 3 }) {
  const color = accentColor(isDark);

  return (
    <Box sx={{
      position: 'absolute',
      inset: 8,
      zIndex,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px dashed ${color}`,
      borderRadius: 2,
      backgroundColor: isDark ? 'rgba(26, 37, 47, 0.8)' : 'rgba(255, 255, 255, 0.8)',
      pointerEvents: 'none'
    }}>
      <Typography sx={{
        maxWidth: '80%',
        px: 2,
        py: 1,
        borderRadius: 1,
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '0.9rem',
        backgroundColor: color,
        color: isDark ? '#1a252f' : '#ffffff'
      }}>
        {message || (label ? `Déposer « ${label} » ici` : 'Déposer ici')}
      </Typography>
    </Box>
  );
}
